import React, { useContext, useEffect, useState } from "react";
import { Button, message, Modal, Badge, Select } from "antd";
import { useAtom } from "jotai";
import { SolicitudAusenciaService } from "services";
import { ApiResponse } from "enum";
import produce from "immer";
import styled from "@emotion/styled";
import { FormProvider, useForm } from "react-hook-form";
import { VisorFormulario } from "components/common";
import {
	solicitudAusenciaSeleccionadaAtom,
	solicitudesAusenciaPendientesAprobarAtom,
	visibleModalVerDocumentoAdjuntoAtom,
	urlDocumentoAdjuntoAtom
} from "../../SolicitudesPendientesAprobarStore";
import { SecurityContext } from "context/SecurityContextProvider";

const ModalVerDocumentoAdjunto = () => {
	const { getUsuarioAutenticado } = useContext(SecurityContext);
	const [solicitudAusenciaSeleccionada, setSolicitudAusenciaSeleccionada] = useAtom(solicitudAusenciaSeleccionadaAtom);
	const [, setSolicitudesPendientesAprobar] = useAtom(solicitudesAusenciaPendientesAprobarAtom);
	const [visibleVerDocumentoAdjunto, setVisibleModalVerDocumentoAdjunto] = useAtom(visibleModalVerDocumentoAdjuntoAtom);
	const [urlDocumento, setUrlDocumento] = useAtom(urlDocumentoAdjuntoAtom);
	const frmVisor = useForm();
	const [tipoEnfermedad, setTipoEnfermedad] = useState(null);
	const [tiposEnfermedad, setTiposEnfermedad] = useState([]);
	const [loadingGuardar, setLoadingGuardar] = useState(false);

	const esDescansoMedico =
		solicitudAusenciaSeleccionada?.tipo?.descripcion?.toLowerCase().includes("descanso m") ?? false;
	const esSDF =
		getUsuarioAutenticado().empresaSeleccionada?.ruc === "20330791684" ||
		getUsuarioAutenticado().empresaSeleccionada?.ruc === "20512436090";

	useEffect(() => {
		if (visibleVerDocumentoAdjunto) {
			setTipoEnfermedad(solicitudAusenciaSeleccionada?.tipoEnfermedad ?? null);
			setTiposEnfermedad([]);
			if (esDescansoMedico && esSDF) {
				SolicitudAusenciaService.listarTiposEnfermedad()
					.then((response) => {
						if (response.estado === "EXITO") {
							setTiposEnfermedad(response.data);
						}
					})
					.catch(() => {});
			}
		}
	}, [visibleVerDocumentoAdjunto]);

	const onClickBtnCancelar = () => {
		setVisibleModalVerDocumentoAdjunto(false);
	};

	const onClickVerPDF = async (url) => {
		console.log("Url", url);
		const response = await SolicitudAusenciaService.verDocumentoAdjunto({
			urlDocumento: url
		});
		setUrlDocumento(response.urlPlantilla)
		console.log("response", response.urlPlantilla);
	};

	const onClickGuardarTipoEnfermedad = async () => {
		setLoadingGuardar(true);
		try {
			const response = await SolicitudAusenciaService.guardarTipoEnfermedad({
				solicitudAusenciaId: solicitudAusenciaSeleccionada.id,
				tipoEnfermedad: tipoEnfermedad,
			});
			if (response.estado === ApiResponse.EXITO) {
				message.success("Tipo de enfermedad guardado correctamente.");
				// Actualizar el atom de la lista para que la columna refleje el cambio inmediatamente
				setSolicitudesPendientesAprobar(
					produce((draft) => {
						const item = draft.find((s) => s.id === solicitudAusenciaSeleccionada.id);
						if (item) item.tipoEnfermedad = tipoEnfermedad;
					})
				);
				// Actualizar también la solicitud seleccionada para que re-abrir el modal pre-populate correctamente
				setSolicitudAusenciaSeleccionada({ ...solicitudAusenciaSeleccionada, tipoEnfermedad });
			} else {
				message.error(response.mensaje || "No se pudo guardar el tipo de enfermedad.");
			}
		} catch (e) {
			message.error("No se pudo guardar el tipo de enfermedad.");
		}
		setLoadingGuardar(false);
	};

	return (
		<Modal
			title={
				<div>
					Documentos Adjuntos
				</div>
			}
			open={visibleVerDocumentoAdjunto}
			closable={true}
			onCancel={onClickBtnCancelar}
			width="calc(100vw - 150px)"
			destroyOnClose={true}
			footer={null}
			centered
		>
			<ContainerPrincipal>
				<Container>
					<Titulo>
						<Badge status="success" text="Documentos" />
					</Titulo>
					{esDescansoMedico && esSDF && (
						<SeccionTipoEnfermedad>
							<span style={{ display: "block", marginBottom: "4px", fontWeight: 500 }}>Tipo de Enfermedad</span>
							<Select
								placeholder="Seleccione el tipo de enfermedad"
								value={tipoEnfermedad}
								onChange={(value) => setTipoEnfermedad(value)}
								style={{ width: "100%" }}
							>
								{tiposEnfermedad.map((tipo) => (
									<Select.Option key={tipo.id} value={tipo.id}>
										{tipo.descripcion}
									</Select.Option>
								))}
							</Select>
							<Button
								type="primary"
								loading={loadingGuardar}
								disabled={!tipoEnfermedad}
								onClick={onClickGuardarTipoEnfermedad}
								style={{ marginTop: "8px", width: "100%" }}
							>
								Guardar
							</Button>
						</SeccionTipoEnfermedad>
					)}
					{solicitudAusenciaSeleccionada?.adjuntos && Object.keys(solicitudAusenciaSeleccionada.adjuntos).map((key) => {
						return (
							<Button key={key} style={{ marginTop: "10px", marginLeft: "5px" }} onClick={() => onClickVerPDF(solicitudAusenciaSeleccionada.adjuntos[key])}>{key}</Button>
						);
					})}
				</Container>
				<ContainerVisor>
					<Body withHeader={false}>
						<FormProvider {...frmVisor}>
							<VisorFormulario
								urlPdf={urlDocumento}
								form={frmVisor}
							/>
						</FormProvider>
					</Body>
				</ContainerVisor>
			</ContainerPrincipal>
		</Modal>
	);
};

const ContainerPrincipal = styled.div`
	display: flex;
	flex-wrap: "wrap";
	justify-content: space-between;
	height: calc(100vh - 200px);
`;

const Container = styled.div`
	border: 1px solid #ccc;
	flex: 1 1 25%;
	margin: 10px;
	background-color: white;
	overflow-y: auto;
`;

const ContainerVisor = styled.div`
	flex: 1 1 70%;
	margin: 10px;
`;

const Body = styled.div`
	height: ${(props) => (props.withHeader === true ? "calc(100% - 50px)" : "100%")};
`;

const Titulo = styled.div`
	flex: 1 1 20%;
	padding: 10px;
	border-bottom: 1px solid #ccc;
`;

const SeccionTipoEnfermedad = styled.div`
	padding: 10px;
	border-top: 1px solid #e8e8e8;
	margin-top: 10px;
`;

export default ModalVerDocumentoAdjunto;
